import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Typography, Box, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledPre = styled('pre')(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  overflowX: 'auto',
  '& code': {
    fontFamily: 'monospace',
  },
}));

const StyledBlockquote = styled(Box)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.grey[300]}`,
  paddingLeft: theme.spacing(2),
  margin: theme.spacing(2, 0),
  color: theme.palette.text.secondary,
}));

const StyledTable = styled('table')(({ theme }) => ({
  borderCollapse: 'collapse',
  width: '100%',
  marginBottom: theme.spacing(2),
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const MarkdownComponents = {
  p: ({ children }) => (
    <Typography variant="body1" sx={{ mb: 0, fontFamily: 'Inter, sans-serif' }}>
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography
      variant="h4"
      sx={{
        mb: 2,
        mt: 2,
        fontFamily: 'Cabinet Grotesk, sans-serif',
        fontWeight: 700,
      }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography
      variant="h5"
      sx={{
        mb: 2,
        mt: 2,
        fontFamily: 'Cabinet Grotesk, sans-serif',
        fontWeight: 700,
      }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      variant="h6"
      sx={{
        mb: 1,
        mt: 1,
        fontFamily: 'Cabinet Grotesk, sans-serif',
        fontWeight: 700,
      }}
    >
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography
      variant="subtitle1"
      sx={{
        mb: 1,
        mt: 1,
        fontFamily: 'Cabinet Grotesk, sans-serif',
        fontWeight: 700,
      }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Typography
      component="strong"
      sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
    >
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Typography component="ul" sx={{ mb: 0, pl: 2 }}>
      {children}
    </Typography>
  ),
  ol: ({ children }) => (
    <Typography component="ol" sx={{ mb: 0, pl: 2 }}>
      {children}
    </Typography>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body1">
      {children}
    </Typography>
  ),
  blockquote: ({ children }) => <StyledBlockquote>{children}</StyledBlockquote>,
  code: ({ node, inline, children }) => {
    if (inline) {
      return (
        <Typography
          component="code"
          sx={{
            backgroundColor: 'grey.100',
            padding: '2px 4px',
            borderRadius: 1,
            fontFamily: 'monospace',
          }}
        >
          {children}
        </Typography>
      );
    }
    return (
      <StyledPre>
        <code>{children}</code>
      </StyledPre>
    );
  },
  table: ({ children }) => <StyledTable>{children}</StyledTable>,
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => (
    <th>
      <Typography variant="subtitle2">{children}</Typography>
    </th>
  ),
  td: ({ children }) => (
    <td>
      <Typography variant="body2">{children}</Typography>
    </td>
  ),
  a: ({ href, children }) => (
    <Typography
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        color: 'primary.main',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
    >
      {children}
    </Typography>
  ),
};

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  return (
    <ReactMarkdown components={MarkdownComponents}>{content}</ReactMarkdown>
  );
};

export default MarkdownRenderer;
